import React from "react";
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from "@mui/material";

export type HealthStatus = "on-track" | "monitor" | "needs-attention" | "no-data";

export interface StudyCardData {
  study_id: number;
  study_code: string;
  study_name: string | null;
  researcher_email: string | null;
  study_status: string;
  study_type: string;
  enrollment_target: number | null;
  enrollment_deadline: string | null;
  invited: number;
  accepted: number;
  connected: number;
  active: number;
  completed: number;
  withdrawn: number;
  expired: number;
  total: number;
  enrolled: number;
  health_status: HealthStatus;
  health_reason: string;
  last_activity_at: string | null;
  last_participant_activity: string | null;
}

const HEALTH_COLORS: Record<HealthStatus, string> = {
  "on-track": "#2E7D32",
  "monitor": "#ED6C02",
  "needs-attention": "#D32F2F",
  "no-data": "#9E9E9E",
};

const HEALTH_LABELS: Record<HealthStatus, string> = {
  "on-track": "On track",
  "monitor": "Monitor",
  "needs-attention": "Needs attention",
  "no-data": "No data",
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "No activity";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function formatPIName(email: string | null): string {
  if (!email) return "—";
  const local = email.split("@")[0];
  // Try to extract a readable name from email
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) {
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).slice(0, 2).join(" ");
  }
  return local;
}

interface StudyCardProps {
  study: StudyCardData;
  onClick: (study: StudyCardData) => void;
}

const StudyCard: React.FC<StudyCardProps> = ({ study, onClick }) => {
  const healthColor = HEALTH_COLORS[study.health_status];
  const progressPct = study.enrollment_target
    ? Math.min(100, Math.round((study.enrolled / study.enrollment_target) * 100))
    : null;

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        transition: "box-shadow 0.2s, transform 0.15s",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-2px)",
        },
        borderLeft: `4px solid ${healthColor}`,
      }}
    >
      <CardActionArea onClick={() => onClick(study)} sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Header: health dot + study name */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: healthColor,
                flexShrink: 0,
                ...(study.health_status === "needs-attention" && {
                  animation: "pulse-dot 2s infinite",
                  "@keyframes pulse-dot": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }),
              }}
            />
            <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1 }}>
              {study.study_name || study.study_code}
            </Typography>
          </Stack>

          {/* Status phrase */}
          <Typography variant="caption" sx={{ color: healthColor, fontWeight: 500, mb: 1 }}>
            {HEALTH_LABELS[study.health_status]}
            {study.health_reason !== HEALTH_LABELS[study.health_status] ? ` — ${study.health_reason}` : ""}
          </Typography>

          {/* Enrollment */}
          <Box sx={{ mb: 1, mt: "auto" }}>
            <Typography variant="body2" fontWeight={500}>
              {study.enrollment_target
                ? `${study.enrolled} / ${study.enrollment_target} enrolled`
                : `${study.enrolled} enrolled`}
            </Typography>
            {progressPct !== null && (
              <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: "grey.200", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${progressPct}%`,
                    borderRadius: 2,
                    bgcolor: healthColor,
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Footer: PI + last activity */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: "55%" }}>
              {formatPIName(study.researcher_email)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatRelativeTime(study.last_activity_at)}
            </Typography>
          </Stack>

          {/* Study status chip */}
          {study.study_status !== "active" && (
            <Chip
              size="small"
              label={study.study_status}
              sx={{ mt: 1, alignSelf: "flex-start", fontSize: "0.65rem", height: 20 }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default StudyCard;
